import { useModal } from '@hooks/use-modal'
import { Button } from '@ui/components/button'

import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppContext } from '@hooks/use-app-context'
import { useApi } from '@hooks/use-api-requests.hook'

import { Actions } from '@store/reducer'
import { useModUnico } from '@hooks/use-mod-unico.hook'
import { log } from '@utils/debug'

import { getError } from '@utils/validators'
import { forceScrollHeight } from '@hooks/use-resize'
import type { UserDocument } from '@hooks/type-ds'
import { _useReset, type UploadError } from '../../components/use-upload.hook'
import { InputUpload } from '@ui/components/input-upload'
import { FileUploadErrorComponent } from '../../components/modal-views/file-upload.component'

type UploadStatus = {
	status: null | boolean
	isFilled: boolean
	shouldUpload: boolean
	id: UserDocument
	hasBeenUploaded: boolean
}

type UploadInputComponentProps = {
	documents: Array<UserDocument>
	maxDocs: number
	minDocs: number
	title: string
	onError: React.Dispatch<React.SetStateAction<UploadError>>
	areDocsQueued: ((status: boolean) => void) | undefined
	onUpload: (currentUpload: UploadStatus) => void
	shouldUpload: boolean
	id: UserDocument
}

const UploadInputComponent: React.FC<UploadInputComponentProps> = ({
	documents,
	maxDocs,
	minDocs,
	title,
	onError,
	onUpload,
	areDocsQueued,
	shouldUpload,
	id,
}) => {
	if (!documents.includes(id)) {
		return (
			<InputUpload
				id={id}
				maxDocs={maxDocs}
				minDocs={minDocs}
				title={title}
				onError={onError}
				onUpload={onUpload}
				areDocsQueued={areDocsQueued}
				shouldUpload={shouldUpload}
			/>
		)
	}

	return null
}

export const UploadModalThanks = () => {
	const { close } = useModal()
	const { state, dispatch } = useAppContext()

	const [isUploading, setUploading] = useState(false)
	const [hasError, setError] = useState<UploadError>(getError('NONE'))
	const resetFile = _useReset(setError)

	const [ideSTATUS, setIdeSTATUS] = useState<UploadStatus>({
		isFilled: false,
		status: null,
		shouldUpload: false,
		hasBeenUploaded: false,
		id: 'IDE',
	})

	const [cfSTATUS, setCfSTATUS] = useState<UploadStatus>({
		isFilled: false,
		status: null,
		shouldUpload: false,
		hasBeenUploaded: false,
		id: 'CF',
	})

	const [altroSTATUS, setAltroSTATUS] = useState<UploadStatus>({
		isFilled: false,
		status: null,
		shouldUpload: false,
		hasBeenUploaded: false,
		id: 'ALTRO',
	})

	const statuses = [ideSTATUS, cfSTATUS, altroSTATUS]
	const triggers = [setIdeSTATUS, setCfSTATUS, setAltroSTATUS]

	const CUDLabel = useModUnico()

	const [fpgesudo, sendFPGESUDO] = useApi({
		request: 'DbOperations/SendToFPgesudo',
		method: 'POST',
		sleeper: true,
	})

	const requiredKeys = useMemo<Array<UserDocument>>(
		() => ['CF', 'IDE', 'ALTRO'],
		[]
	)

	const [documents] = useApi<Array<UserDocument>>({
		request: 'DbOperations/GetDocumentsUploadStatus',
		method: 'POST',
		body: {
			idRichiesta: state.data.IdRichiesta.toString(),
			idSessione: state.data.sessione?.toString(),
			idSessioneMobile: state.mobileToken?.toString(),
		},
		shouldCache: false,
	})

	useEffect(() => {
		if (fpgesudo !== null) {
			// TODO: GESUDO sta a fa qualcosa
			const keysInState = Object.keys(state.files)
			const areKeyInState = requiredKeys.every(
				(key: UserDocument) =>
					documents?.includes(key) || keysInState?.includes(key)
			)

			console.log(
				'gesudodebug',
				keysInState,
				areKeyInState,
				requiredKeys,
				documents,
				keysInState
			)

			if (areKeyInState) {
				dispatch({
					type: Actions.SetThanksDone,
					payload: true,
				})
			}

			setUploading(false)
			close()
		}
	}, [fpgesudo])

	useEffect(() => {
		const noPendings = statuses.some((doc) => doc.isFilled && doc.status)
		log('no-pendings', noPendings, statuses)
		if (noPendings) {
			const isDone = statuses.every((doc) => {
				if (doc.isFilled === false) {
					return true
				}

				return doc.isFilled && doc.status
			})

			log('is-done', isDone)

			if (isDone) {
				sendFPGESUDO({
					body: {
						NumeroPratica: state.ocs.NumeroPratica?.toString(),
						CodiceControparte: state.ocs.Controparte?.toString(),
					},
				})
			}
		}
	}, [ideSTATUS, cfSTATUS, altroSTATUS])

	useEffect(() => {
		if (documents) {
			statuses.forEach((status, idx) => {
				if (documents.includes(status.id)) {
					triggers[idx]({
						...status,
						shouldUpload: false,
						hasBeenUploaded: true,
					})
				}
			})
		}
	}, [documents])

	const setQueue = (currentUpload: UploadStatus) => {
		const currentIdx = statuses.findIndex((e) => e.shouldUpload)
		const currentDispatch = triggers[currentIdx]

		const nextN = statuses.findIndex(
			(e) =>
				e.isFilled && e.status === null && !e.shouldUpload && !e.hasBeenUploaded
		)
		const [nextStatus, nextDispatch] = [statuses[nextN], triggers[nextN]]
		currentDispatch(currentUpload)

		if (nextDispatch) {
			nextDispatch({
				...nextStatus,
				shouldUpload: true,
			})
		}
	}

	if (hasError.type !== 'NONE') {
		return (
			<div className='uploadthanks-modal__error'>
				<FileUploadErrorComponent
					type={hasError.type}
					title={hasError.title}
					description={hasError.description}
					id={'ALTRO'}
					reset={resetFile}
					closeModal={close}
				/>
			</div>
		)
	}

	if (documents) {
		forceScrollHeight()

		return (
			<section>
				<div
					className={clsx('uploadthanks-modal__loader', {
						'uploadthanks-modal--hidden': !isUploading,
					})}
				>
					{/* <Loader force /> */}
				</div>

				<div
					className={clsx('uploadthanks-modal', {
						'uploadthanks-modal--hidden': isUploading,
					})}
				>
					<p className={'carta-id__container--title'}>
						Carica i tuoi documenti
					</p>
					<div className='carta-id__container--margin'>
						<p>
							Per proseguire allega una copia fronte e retro dei documenti in
							formato{' '}
							<span className={'carta-id__container--bold'}>
								JPG, PNG, TIFF, HEIC/HEIF o PDF
							</span>{' '}
							di{' '}
							<span className={'carta-id__container--bold'}>
								dimensione non superiore a 5MB.
							</span>
						</p>
						<p>
							I documenti devono essere chiaramente leggibili e non devono
							contenere riflessi.
						</p>
					</div>
					<div className='uploadthanks-modal__inputs'>
						<UploadInputComponent
							id='IDE'
							documents={documents}
							maxDocs={2}
							minDocs={1}
							title='Carta di identità'
							onError={setError}
							areDocsQueued={(isFilled) =>
								setIdeSTATUS({ ...ideSTATUS, isFilled })
							}
							onUpload={setQueue}
							shouldUpload={ideSTATUS.shouldUpload}
						/>

						<UploadInputComponent
							id='CF'
							documents={documents}
							maxDocs={2}
							minDocs={1}
							title='Codice fiscale'
							onError={setError}
							areDocsQueued={(isFilled) =>
								setCfSTATUS({ ...cfSTATUS, isFilled })
							}
							onUpload={setQueue}
							shouldUpload={cfSTATUS.shouldUpload}
						/>

						<UploadInputComponent
							documents={documents}
							id='ALTRO'
							maxDocs={1000000}
							minDocs={1}
							title={CUDLabel}
							onError={setError}
							onUpload={setQueue}
							areDocsQueued={(isFilled) => {
								console.log('set-status', isFilled, altroSTATUS)
								setAltroSTATUS({ ...altroSTATUS, isFilled })
							}}
							shouldUpload={altroSTATUS.shouldUpload}
						/>
					</div>
					<Button
						label={'procedi'}
						className={'uploadthanks-modal__button carta-id__container-btn'}
						disabled={
							!ideSTATUS.isFilled && !cfSTATUS.isFilled && !altroSTATUS.isFilled
						}
						onClick={() => {
							setUploading(true)
							const trigger =
								triggers[statuses.findIndex((status) => status.isFilled)]
							if (trigger) {
								trigger({
									...statuses.find((status) => status.isFilled)!, // eslint-disable-line
									shouldUpload: true,
								})
							}
						}}
					/>
				</div>
			</section>
		)
	}

	return (
		<div className='uploadthanks-modal__loader'>
			{/* <Loader force /> */}
		</div>
	)
}
